import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Link2, Unlink } from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const CATEGORIES = [
  "Servizi Funerari",
  "Trasporti",
  "Fiori e Decorazioni",
  "Pratiche Amministrative",
  "Personale",
  "Struttura",
  "Altro"
];

const Costs = () => {
  const { getAuthHeaders } = useAuth();
  const [costs, setCosts] = useState([]);
  const [funeralServices, setFuneralServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    category: "",
    date: new Date().toISOString().split("T")[0],
    notes: "",
    funeral_service_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [costsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/costs`, getAuthHeaders()),
        axios.get(`${API}/funeral-services`, getAuthHeaders())
      ]);
      setCosts(costsRes.data);
      setFuneralServices(servicesRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        funeral_service_id: formData.funeral_service_id || null
      };

      if (editingCost) {
        await axios.put(`${API}/costs/${editingCost.id}`, data, getAuthHeaders());
        toast.success("Costo aggiornato");
      } else {
        await axios.post(`${API}/costs`, data, getAuthHeaders());
        toast.success("Costo aggiunto");
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo costo?")) return;
    try {
      await axios.delete(`${API}/costs/${id}`, getAuthHeaders());
      toast.success("Costo eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);
    setFormData({
      description: cost.description,
      amount: cost.amount.toString(),
      category: cost.category,
      date: cost.date,
      notes: cost.notes || "",
      funeral_service_id: cost.funeral_service_id || ""
    });
    setIsDialogOpen(true);
  };

  const handleUnlinkCost = async (cost) => {
    try {
      await axios.put(`${API}/costs/${cost.id}`, {
        ...cost,
        funeral_service_id: null
      }, getAuthHeaders());
      toast.success("Costo scollegato dal servizio");
      fetchData();
    } catch (error) {
      toast.error("Errore nello scollegamento");
    }
  };

  const resetForm = () => {
    setEditingCost(null);
    setFormData({
      description: "",
      amount: "",
      category: "",
      date: new Date().toISOString().split("T")[0],
      notes: "",
      funeral_service_id: ""
    });
  };

  const getServiceName = (serviceId) => {
    const service = funeralServices.find(s => s.id === serviceId);
    return service ? service.deceased_name : null;
  };

  const filteredCosts = costs.filter(cost => {
    const matchesSearch = cost.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cost.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || cost.category === categoryFilter;
    const matchesService = serviceFilter === "all" || 
                          (serviceFilter === "unlinked" && !cost.funeral_service_id) ||
                          cost.funeral_service_id === serviceFilter;
    return matchesSearch && matchesCategory && matchesService;
  });

  const totalAmount = filteredCosts.reduce((sum, cost) => sum + cost.amount, 0);
  const linkedCosts = costs.filter(c => c.funeral_service_id).length;
  const unlinkedCosts = costs.filter(c => !c.funeral_service_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="costs-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Costi Funerali</h1>
          <p className="page-subtitle">Gestione delle spese operative</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full px-6"
              data-testid="add-cost-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Costo
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-2xl border-0 shadow-2xl sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-[#1D1D1F]">
                {editingCost ? "Modifica Costo" : "Nuovo Costo"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Descrizione</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Es: Servizio funebre completo"
                  className="h-12 rounded-xl border-[#D2D2D7]"
                  data-testid="cost-description"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Importo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0,00"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="cost-amount"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Data</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="cost-date"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Categoria</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="cost-category">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F] flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Collega a Servizio Funebre
                </Label>
                <Select
                  value={formData.funeral_service_id}
                  onValueChange={(value) => setFormData({ ...formData, funeral_service_id: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="cost-service">
                    <SelectValue placeholder="Nessun servizio (opzionale)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                    <SelectItem value="">Nessun servizio</SelectItem>
                    {funeralServices.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.deceased_name} ({new Date(s.service_date).toLocaleDateString('it-IT')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#86868B]">
                  Collegando il costo a un servizio, verrà incluso nel calcolo delle percentuali
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Note (opzionale)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="h-12 rounded-xl border-[#D2D2D7]"
                  data-testid="cost-notes"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 h-12 rounded-full border-[#D2D2D7]"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full"
                  data-testid="save-cost-btn"
                >
                  {editingCost ? "Aggiorna" : "Salva"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & Summary */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-4 w-full md:w-auto flex-wrap">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868B]" />
                <Input
                  placeholder="Cerca costi..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 rounded-xl border-[#D2D2D7]"
                  data-testid="search-costs"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48 h-11 rounded-xl border-[#D2D2D7]" data-testid="filter-category">
                  <SelectValue placeholder="Tutte le categorie" />
                </SelectTrigger>
                <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger className="w-56 h-11 rounded-xl border-[#D2D2D7]" data-testid="filter-service">
                  <SelectValue placeholder="Tutti i servizi" />
                </SelectTrigger>
                <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                  <SelectItem value="all">Tutti i servizi</SelectItem>
                  <SelectItem value="unlinked">Non collegati ({unlinkedCosts})</SelectItem>
                  {funeralServices.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.deceased_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#86868B]">Totale filtrato</p>
              <p className="text-2xl font-semibold text-[#1D1D1F]">{formatCurrency(totalAmount)}</p>
              <p className="text-xs text-[#86868B]">
                <span className="text-[#34C759]">{linkedCosts} collegati</span> • <span className="text-[#FF9500]">{unlinkedCosts} non collegati</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Costs Table */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <CardContent className="p-0">
          <Table data-testid="costs-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Descrizione
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Categoria
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Servizio Collegato
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Data
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Importo
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Azioni
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCosts.length > 0 ? (
                filteredCosts.map((cost) => (
                  <TableRow key={cost.id} className="hover:bg-[#F5F5F7]/30">
                    <TableCell className="px-6 py-4">
                      <div>
                        <p className="font-medium text-[#1D1D1F]">{cost.description}</p>
                        {cost.notes && (
                          <p className="text-sm text-[#86868B] mt-1">{cost.notes}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#F5F5F7] text-[#1D1D1F]">
                        {cost.category}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {cost.funeral_service_id ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#34C759]/10 text-[#34C759] border-0">
                            <Link2 className="w-3 h-3 mr-1" />
                            {getServiceName(cost.funeral_service_id)}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUnlinkCost(cost)}
                            className="h-6 w-6 rounded hover:bg-[#FF3B30]/10"
                            title="Scollega"
                          >
                            <Unlink className="w-3 h-3 text-[#FF3B30]" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-[#86868B] text-sm">Non collegato</span>
                      )}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-[#86868B]">
                      {new Date(cost.date).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right font-semibold text-[#FF3B30]">
                      {formatCurrency(cost.amount)}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(cost)}
                          className="h-9 w-9 rounded-lg hover:bg-[#007AFF]/10"
                          data-testid={`edit-cost-${cost.id}`}
                        >
                          <Pencil className="w-4 h-4 text-[#007AFF]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(cost.id)}
                          className="h-9 w-9 rounded-lg hover:bg-[#FF3B30]/10"
                          data-testid={`delete-cost-${cost.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-[#86868B]">
                    Nessun costo trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Costs;
